import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

/* ------------------------------------------------------------------ */
/*  Color map for dynamic Tailwind classes (avoids purge issues)       */
/* ------------------------------------------------------------------ */
const colorMap = {
  primary: { bg: 'bg-primary-50', text: 'text-primary-600', icon: 'text-primary-500' },
  green:   { bg: 'bg-green-50',   text: 'text-green-600',   icon: 'text-green-500' },
  cyan:    { bg: 'bg-blue-50',    text: 'text-blue-600',    icon: 'text-blue-500' },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-600',  icon: 'text-purple-500' },
  yellow:  { bg: 'bg-yellow-50',  text: 'text-yellow-600',  icon: 'text-yellow-500' },
  pink:    { bg: 'bg-red-50',     text: 'text-red-600',     icon: 'text-red-500' },
};

/* ------------------------------------------------------------------ */
/*  Trend arrow component                                              */
/* ------------------------------------------------------------------ */
function TrendIndicator({ value }) {
  if (value == null) return null;
  const isUp = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-caption font-semibold ${isUp ? 'text-green-600' : 'text-red-600'}`}>
      {isUp ? (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 4.5l15 15m0 0V8.25m0 11.25H8.25" />
        </svg>
      )}
      {isUp ? '+' : ''}{value}%
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Stripe-style metric card                                           */
/* ------------------------------------------------------------------ */
function MetricCard({ title, value, change, icon, color = 'primary', index = 0 }) {
  const colors = colorMap[color] || colorMap.primary;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-5 hover:shadow-soft-md transition-shadow duration-200"
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-overline uppercase tracking-wider text-surface-400">{title}</span>
        <span className={`p-2 rounded-xl ${colors.bg}`}>
          {icon}
        </span>
      </div>
      <p className="text-h2 text-surface-900 font-bold mb-1">{value}</p>
      <TrendIndicator value={change} />
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Dashboard                                                     */
/* ------------------------------------------------------------------ */
const BusinessDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    overview: {},
    recentEvents: [],
    analytics: {},
    pendingActions: [],
    promotions: [],
  });
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  /* ----- Mock data ----- */
  const mockDashboardData = {
    overview: {
      totalEvents: 45,
      totalAttendees: 1250,
      upcomingEvents: 8,
      revenue: 12500,
      averageRating: 4.6,
      followers: 234,
    },
    recentEvents: [
      {
        id: 1,
        title: 'Wine Tasting Evening',
        date: new Date(Date.now() + 2 * 86400000),
        attendees: 15,
        maxAttendees: 20,
        revenue: 825,
        status: 'active',
      },
      {
        id: 2,
        title: 'Cooking Masterclass',
        date: new Date(Date.now() + 5 * 86400000),
        attendees: 8,
        maxAttendees: 12,
        revenue: 640,
        status: 'active',
      },
      {
        id: 3,
        title: 'Art Gallery Opening',
        date: new Date(Date.now() - 3 * 86400000),
        attendees: 25,
        maxAttendees: 30,
        revenue: 500,
        status: 'completed',
      },
    ],
    analytics: {
      totalViews: 5420,
      conversionRate: 12.5,
      repeatCustomers: 34,
      avgEventRating: 4.6,
      monthlyGrowth: 8.5,
      topCategories: [
        { name: 'Food & Drink', percentage: 45 },
        { name: 'Arts & Culture', percentage: 30 },
        { name: 'Education', percentage: 25 },
      ],
    },
    pendingActions: [
      {
        id: 1,
        type: 'event_approval',
        title: 'Summer Festival requires approval',
        description: 'Partnership event with City Events needs your approval',
        actionUrl: '/business/partnerships',
        urgent: true,
      },
      {
        id: 2,
        type: 'review_response',
        title: '3 reviews need responses',
        description: 'Recent event reviews are waiting for your response',
        actionUrl: '/business/reviews',
        urgent: false,
      },
      {
        id: 3,
        type: 'payment_issue',
        title: 'Payment method needs update',
        description: 'Your default payment method expires in 5 days',
        actionUrl: '/business/billing',
        urgent: true,
      },
    ],
    promotions: [
      {
        id: 1,
        title: 'Early Bird Special - Cooking Class',
        discount: 25,
        redemptions: 12,
        budget: 500,
        spent: 180,
        endsAt: new Date(Date.now() + 5 * 86400000),
        status: 'active',
      },
      {
        id: 2,
        title: 'Weekend Wine Tasting',
        discount: 20,
        redemptions: 8,
        budget: 300,
        spent: 120,
        endsAt: new Date(Date.now() + 2 * 86400000),
        status: 'active',
      },
    ],
  };

  /* ----- Data fetching (useCallback BEFORE useEffect) ----- */
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // Replace with actual API call:
      // const response = await api.get('/api/business/dashboard', { params: { period: selectedPeriod } });
      // setDashboardData(response.data);
      setDashboardData(mockDashboardData);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
      setDashboardData(mockDashboardData);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  /* ----- Formatters ----- */
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const formatDate = (date) =>
    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);

  /* ----- Loading state ----- */
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-surface-200 border-t-primary-500" />
        <p className="text-body-sm text-surface-400">Loading dashboard...</p>
      </div>
    );
  }

  const { overview, recentEvents, analytics, pendingActions, promotions } = dashboardData;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-h1 text-surface-900">Business Dashboard</h1>
          <p className="text-body text-surface-500 mt-1">Manage your events and track performance</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="bg-surface-50 border border-surface-200 rounded-xl px-4 py-2.5 text-body-sm text-surface-700 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:outline-none transition-all duration-200"
            aria-label="Select time period"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <Link
            to="/create-event"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 transition-colors duration-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Create Event
          </Link>
        </div>
      </motion.div>

      {/* Pending Actions */}
      {pendingActions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-warning-50 rounded-2xl border border-warning-400/30 p-5"
        >
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-5 h-5 text-warning-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <h3 className="text-body font-semibold text-warning-600">Pending Actions</h3>
          </div>
          <div className="space-y-2">
            {pendingActions.map((action) => (
              <div
                key={action.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white/60 rounded-xl px-4 py-3"
              >
                <div className="flex items-start gap-2">
                  {action.urgent && (
                    <span className="mt-1.5 w-2 h-2 bg-error-500 rounded-full flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-body-sm font-medium text-surface-800">{action.title}</p>
                    <p className="text-caption text-surface-500">{action.description}</p>
                  </div>
                </div>
                <Link
                  to={action.actionUrl}
                  className="inline-flex items-center px-3.5 py-1.5 bg-warning-500 text-white text-caption font-semibold rounded-lg hover:bg-warning-600 transition-colors duration-200 whitespace-nowrap self-start sm:self-center"
                >
                  Take Action
                </Link>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard
          index={0}
          title="Total Events"
          value={overview.totalEvents}
          change={8.5}
          color="primary"
          icon={<svg className="w-4.5 h-4.5 text-primary-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>}
        />
        <MetricCard
          index={1}
          title="Attendees"
          value={overview.totalAttendees?.toLocaleString()}
          change={15.2}
          color="green"
          icon={<svg className="w-4.5 h-4.5 text-green-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>}
        />
        <MetricCard
          index={2}
          title="Revenue"
          value={formatCurrency(overview.revenue || 0)}
          change={12.3}
          color="cyan"
          icon={<svg className="w-4.5 h-4.5 text-blue-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <MetricCard
          index={3}
          title="Upcoming"
          value={overview.upcomingEvents}
          color="purple"
          icon={<svg className="w-4.5 h-4.5 text-purple-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg>}
        />
        <MetricCard
          index={4}
          title="Rating"
          value={`${overview.averageRating || 0}/5`}
          change={2.1}
          color="yellow"
          icon={<svg className="w-4.5 h-4.5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>}
        />
        <MetricCard
          index={5}
          title="Followers"
          value={overview.followers}
          change={5.8}
          color="pink"
          icon={<svg className="w-4.5 h-4.5 text-red-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" /></svg>}
        />
      </div>

      {/* Two-column layout: Recent Events + Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Events -- table on desktop, cards on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-5 sm:p-6"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-h3 text-surface-900">Recent Events</h3>
            <Link to="/business/events" className="text-body-sm font-semibold text-primary-500 hover:text-primary-700 transition-colors">
              View All
            </Link>
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-100">
                  <th className="text-left text-overline uppercase tracking-wider text-surface-400 pb-3 font-semibold">Event</th>
                  <th className="text-left text-overline uppercase tracking-wider text-surface-400 pb-3 font-semibold">Date</th>
                  <th className="text-right text-overline uppercase tracking-wider text-surface-400 pb-3 font-semibold">Revenue</th>
                  <th className="text-right text-overline uppercase tracking-wider text-surface-400 pb-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {recentEvents.map((event) => (
                  <tr key={event.id} className="group">
                    <td className="py-3.5">
                      <Link to={`/events/${event.id}`} className="text-body-sm font-medium text-surface-800 group-hover:text-primary-600 transition-colors">
                        {event.title}
                      </Link>
                      <p className="text-caption text-surface-400 mt-0.5">
                        {event.attendees}/{event.maxAttendees} attendees
                      </p>
                    </td>
                    <td className="py-3.5 text-body-sm text-surface-500">{formatDate(event.date)}</td>
                    <td className="py-3.5 text-body-sm font-semibold text-surface-800 text-right">{formatCurrency(event.revenue)}</td>
                    <td className="py-3.5 text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-caption font-semibold ${
                        event.status === 'active'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-surface-100 text-surface-500'
                      }`}>
                        {event.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-3">
            {recentEvents.map((event) => (
              <div key={event.id} className="bg-surface-50 rounded-xl p-4">
                <div className="flex items-start justify-between mb-2">
                  <Link to={`/events/${event.id}`} className="text-body-sm font-medium text-surface-800 hover:text-primary-600 transition-colors">
                    {event.title}
                  </Link>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-caption font-semibold ${
                    event.status === 'active'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-surface-100 text-surface-500'
                  }`}>
                    {event.status}
                  </span>
                </div>
                <div className="flex items-center justify-between text-caption text-surface-500">
                  <span>{formatDate(event.date)}</span>
                  <span className="font-semibold text-surface-700">{formatCurrency(event.revenue)}</span>
                </div>
                <p className="text-caption text-surface-400 mt-1">
                  {event.attendees}/{event.maxAttendees} attendees
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Analytics Overview */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.25 }}
          className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-5 sm:p-6"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-h3 text-surface-900">Analytics Overview</h3>
            <Link to="/business/analytics" className="text-body-sm font-semibold text-primary-500 hover:text-primary-700 transition-colors">
              View Details
            </Link>
          </div>

          <div className="space-y-4">
            {[
              { label: 'Total Views', value: analytics.totalViews?.toLocaleString() },
              { label: 'Conversion Rate', value: `${analytics.conversionRate}%` },
              { label: 'Repeat Customers', value: analytics.repeatCustomers },
              { label: 'Monthly Growth', value: `+${analytics.monthlyGrowth}%`, highlight: true },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <span className="text-body-sm text-surface-500">{item.label}</span>
                <span className={`text-body-sm font-semibold ${item.highlight ? 'text-green-600' : 'text-surface-800'}`}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-surface-100">
            <h4 className="text-overline uppercase tracking-wider text-surface-400 mb-3">Top Categories</h4>
            <div className="space-y-3">
              {analytics.topCategories?.map((category, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-body-sm text-surface-600">{category.name}</span>
                    <span className="text-caption font-semibold text-surface-700">{category.percentage}%</span>
                  </div>
                  <div className="w-full bg-surface-100 rounded-full h-1.5">
                    <div
                      className="bg-primary-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${category.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Active Promotions */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 p-5 sm:p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-h3 text-surface-900">Active Promotions</h3>
          <Link to="/business/promotions" className="text-body-sm font-semibold text-primary-500 hover:text-primary-700 transition-colors">
            Manage Promotions
          </Link>
        </div>

        {promotions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {promotions.map((promotion) => (
              <div
                key={promotion.id}
                className="bg-surface-50 rounded-xl border border-surface-200 p-5 hover:shadow-soft-sm transition-shadow duration-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-body-sm font-semibold text-surface-800">{promotion.title}</h4>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-caption font-semibold bg-green-50 text-green-700">
                    {promotion.discount}% OFF
                  </span>
                </div>

                <div className="space-y-2 text-caption text-surface-500">
                  <div className="flex justify-between">
                    <span>Redemptions</span>
                    <span className="font-semibold text-surface-700">{promotion.redemptions}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Budget</span>
                    <span className="font-semibold text-surface-700">
                      {formatCurrency(promotion.spent)} / {formatCurrency(promotion.budget)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Ends</span>
                    <span className="font-semibold text-surface-700">{formatDate(promotion.endsAt)}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="w-full bg-surface-200 rounded-full h-1.5">
                    <div
                      className="bg-primary-500 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${(promotion.spent / promotion.budget) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="mx-auto w-12 h-12 bg-surface-100 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-surface-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
            </div>
            <p className="text-body text-surface-500 mb-4">No active promotions</p>
            <Link
              to="/business/promotions/create"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
              Create Your First Promotion
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default BusinessDashboard;
